import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import {
  AvailabilityResponse,
  CreateOrderBody,
  CreateOrderResponse,
  OrderResponse,
  QuoteResponse,
} from './direct-booking.models';

/**
 * Client for the public OTA booking API (`/api/v1/ota`):
 * availability, quote, order creation and order polling.
 */
@Injectable({ providedIn: 'root' })
export class DirectBookingService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBase}${environment.otaPath}`;

  getAvailability(apartmentId: string, from: string, to: string): Observable<AvailabilityResponse> {
    const params = new HttpParams().set('from', from).set('to', to);
    return this.http.get<AvailabilityResponse>(
      `${this.base}/listings/${apartmentId}/availability`,
      { params },
    );
  }

  getQuote(
    apartmentId: string,
    from: string,
    to: string,
    guests: number,
    pets = 0,
  ): Observable<QuoteResponse> {
    const params = new HttpParams()
      .set('from', from)
      .set('to', to)
      .set('guests', guests)
      .set('pets', pets);
    return this.http.get<QuoteResponse>(`${this.base}/listings/${apartmentId}/quote`, { params });
  }

  createOrder(apartmentId: string, body: CreateOrderBody): Observable<CreateOrderResponse> {
    return this.http.post<CreateOrderResponse>(
      `${this.base}/listings/${apartmentId}/orders`,
      body,
    );
  }

  getOrder(orderId: string): Observable<OrderResponse> {
    return this.http.get<OrderResponse>(`${this.base}/orders/${orderId}`);
  }

  /** The logged-in guest's orders (authenticated — Bearer token via interceptor). */
  myOrders(): Observable<OrderResponse[]> {
    return this.http.get<OrderResponse[]>(`${this.base}/orders`);
  }

  refundOrder(orderId: string, reason?: string): Observable<OrderResponse> {
    return this.http.post<OrderResponse>(`${this.base}/orders/${orderId}/refund`, { reason });
  }

  /** Polls an order until it reaches a terminal status (or attempts run out). */
  pollOrder(orderId: string, intervalMs = 1800, maxAttempts = 20): Observable<OrderResponse> {
    return new Observable<OrderResponse>((subscriber) => {
      let attempts = 0;
      let timer: ReturnType<typeof setTimeout>;
      const terminal: OrderResponse['status'][] = ['confirmed', 'failed', 'refunded', 'canceled'];

      const tick = () => {
        attempts += 1;
        this.getOrder(orderId).subscribe({
          next: (order) => {
            subscriber.next(order);
            if (terminal.includes(order.status) || attempts >= maxAttempts) {
              subscriber.complete();
            } else {
              timer = setTimeout(tick, intervalMs);
            }
          },
          error: (err) => subscriber.error(err),
        });
      };

      tick();
      return () => clearTimeout(timer);
    });
  }
}
